// Jump-to-the-missing-field for long forms (Uber-style): Save stays tappable,
// and a tap with gaps marks every field that has one red with its message,
// scrolls to the first, and puts the cursor in it. Generalised from the fert
// form's SF-19 fix so every form does it the same way.
//
// Usage:
//   const issues = useFieldIssues(blockers)          // [{ field, message }]
//   <div ref={issues.formRef}> … <div {...issues.box('cartons')}> … {issues.msg('cartons')} …
//   onClick={() => { if (!issues.reveal()) return; save() }}
//   {issues.live}   ← once per form: the screen-reader announcement
//
// The issue list is derived from live form state, so filling a field clears
// its red on the next render — nothing to reset by hand. Marks only show
// after the first refused tap (hide() turns them off again, e.g. after a
// "save & new" clears the form for the next entry).

import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

export interface FieldIssue<F extends string> {
  /** The field the issue belongs to; omit for one the form can't fix (it scrolls to `data-field-issues`). */
  field?: F
  message: string
}

const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'

export function useFieldIssues<F extends string>(issues: FieldIssue<F>[]) {
  const [shown, setShown] = useState(false)
  const [jump, setJump] = useState(0)
  const [announcement, setAnnouncement] = useState('')
  const taps = useRef(0)
  const formRef = useRef<HTMLDivElement>(null)
  const first = issues[0]

  // Scroll after the render that paints the marks, so the box is laid out.
  useEffect(() => {
    if (!jump || !first) return
    const root = formRef.current
    // "First" = highest on the SCREEN, not first in the list: the list's order
    // is the caller's, and jumping past an empty box at the top reads as a bug.
    const marked = new Set(issues.map(i => i.field).filter(Boolean))
    const box = [...(root?.querySelectorAll<HTMLElement>('[data-field]') ?? [])]
      .find(el => marked.has(el.dataset.field as F))
      || root?.querySelector<HTMLElement>('[data-field-issues]')
    if (!box) return
    box.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    box.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true })
    // Only the tap moves the page — not every keystroke after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump])

  /** Call on Save. True = nothing missing, go ahead; false = marked + jumped. */
  const reveal = useCallback((): boolean => {
    if (issues.length === 0) return true
    setShown(true)
    taps.current += 1
    setJump(taps.current)
    // ONE polite announcement for the whole tap — not a role="alert" per
    // field, which reads five gaps out as five interruptions. Same sentence
    // twice is skipped by most readers, so alternate a trailing no-break
    // space (Modal's jump-to-required does the same).
    setAnnouncement(`${issues.length === 1 ? '1 thing needs' : `${issues.length} things need`} attention: ${issues[0].message}`
      + (taps.current % 2 === 0 ? '\u00A0' : ''))
    return false
  }, [issues])

  const hide = useCallback(() => setShown(false), [])

  const messageFor = (field: F) =>
    shown ? issues.find(i => i.field === field)?.message ?? null : null

  /** Spread on the field's wrapper: the jump target + the red ring. */
  const box = (field: F, className?: string) => ({
    'data-field': field,
    className: clsx(className,
      // No ring-offset: an offset paints the page colour behind it, which
      // left a dark gap round the ring on a white page.
      messageFor(field) && 'rounded-lg ring-2 ring-red-500'),
  })

  /** The red message under the field (renders nothing when it's fine). */
  const msg = (field: F) => {
    const m = messageFor(field)
    return m ? <p className="text-xs text-red-500 font-semibold mt-1">{m}</p> : null
  }

  /** Render once per form: the polite live region `reveal()` speaks through. */
  const live = <div aria-live="polite" aria-atomic="true" className="sr-only">{announcement}</div>

  return { formRef, reveal, hide, box, msg, live, shown }
}
